import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './index.module.css';
import DefaultImage from '../../../assets/images/icons/blank_profile_pic.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-regular-svg-icons';
import { faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { editAdminImage, selectUserDetails } from '../../../redux/features/auth/authSlice';
import BeatLoader from 'react-spinners/BeatLoader';
import ClipLoader from 'react-spinners/ClipLoader';
import { changePassword, editAdminProfileImage, editProfileDetails, getUser } from '../../../redux/features/auth/authService';
import { showToast } from '../../../components/ToastMsg';

const Profile = () => {
  const dispatch = useDispatch();
  const { details, isUserDetailsLoading } = useSelector(selectUserDetails);
  const { isChangingPassword } = useSelector((state) => state.auth.adminChangePassword);
  const { isDetailsLoading } = useSelector((state) => state.auth.profileDetailsUpdate);
  const { imageUpdateStatus } = useSelector(editAdminImage);
  const [previewEditThumbnailImage, setPreviewEditThumbnailImage] = useState(null);

  const [credentials, setCredentials] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [errors, setErrors] = useState({ newPassword: '', confirmPassword: '' });
  const [disabledBtn, setBtnDisable] = useState(false);
  const [formData, setFormData] = useState({});
  const [initialData, setInitialData] = useState({});
  const [profileErrors, setProfileErrors] = useState({});

  useEffect(() => {
    if (details) {
      setFormData({
        full_name: details.full_name || '',
        email: details.email || '',
        nic: details.nic || '',
        phone_number: details.phone_number || ''
      });
      setInitialData({
        full_name: details.full_name || '',
        email: details.email || '',
        nic: details.nic || '',
        phone_number: details.phone_number || ''
      });
    }
  }, [details]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setProfileErrors((prev) => ({ ...prev, [name]: '' }));
  };

  if (isUserDetailsLoading) {
    return <p>Loading...</p>;
  }

  if (!details) {
    return <p>No user details found. Please log in.</p>;
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (1MB = 1048576 bytes)
      const maxFileSize = 1048576; // 1MB
      if (file.size > maxFileSize) {
        showToast('error', 'Profile picture size must be less than 1MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewEditThumbnailImage(e.target.result);
      };
      reader.readAsDataURL(file);
      dispatch(editAdminProfileImage({ file }))
        .unwrap()
        .then((response) => {
          if (response.success === true) {
            dispatch(getUser());
          } else {
          }
        })
        .catch((error) => {
        });
    }
  };

  const validatePassword = () => {
    const errors = {};
    if (!credentials.newPassword) {
      errors.newPassword = 'Password is required!'
    }
     if (credentials.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long.'
    }
    if (!credentials.confirmPassword) {
      errors.confirmPassword = 'Confirm Password is required!'
    }
    if (credentials.newPassword !== credentials.confirmPassword) {
      errors.confirmPassword = 'Password & Confirm Password must be matched!'
    }
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return false;
    } else {
      return true;
    }
  }

  const handlePasswordChange = async () => {
    if (validatePassword()) {
      setBtnDisable(true);
      try {
        const passwordData = {
          new_password: credentials.newPassword,
          confirm_password: credentials.confirmPassword
        }
        const response = await dispatch(changePassword(passwordData)).unwrap();
        if (response.success === true) {
          await dispatch(getUser()).unwrap();
          setBtnDisable(false);
          showToast('success', response.message);
        } else {
          setBtnDisable(false);
        }
      }
      catch (error) {
        setBtnDisable(false);
      };
    }
  }

  const validateNicNumber = (nic) => /^(?:\d{9}[xXvV]|\d{12})$/.test(nic);
  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validateNumber = (number) => /^07[0-8][0-9]{7}/.test(number);

  const validateDetails = () => {
    const errors = {};
    if (!formData.full_name) {
      errors.full_name = 'Full Name is required.'
    }
    if (!formData.email) {
      errors.email = 'Email is required.'
    }
    if (formData.email && !validateEmail(formData.email.trim())) {
      errors.email = 'Please add a valid email.'
    }
    if (!formData.nic) {
      errors.nic = 'NIC is required.'
    }
    if (formData.nic && !validateNicNumber(formData.nic.trim())) {
      errors.nic = 'NIC must be a valid number (e.g., 9 digits + V/X or 12 digits).'
    }
    if (!formData.phone_number) {
      errors.phone_number = 'Phone Number is required.'
    } if (formData.phone_number.trim().length !== 10 || !validateNumber(formData.phone_number.trim())) {
      errors.phone_number = 'Please enter a valid Sri Lankan mobile number starting with 07.'
    }
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return false;
    } else {
      return true;
    }
  }

  function getChangedValues(selectedData) {
    const changedValues = {};
    for (const key in selectedData) {
      if (
        key !== 'id' &&
        selectedData.hasOwnProperty(key) &&
        selectedData[key] !== initialData[key]
      ) {
        changedValues[key] = selectedData[key];
      }

      if (key === 'id') {
        changedValues[key] = selectedData[key];
      }
    }
    return changedValues;
  }

  const editProfile = async () => {
    if (validateDetails()) {
      try {
        const data = getChangedValues(formData);
        const hasChanges = Object.keys(data).some(key => key !== 'id');
        if (hasChanges) {
          const response = await dispatch(editProfileDetails(data)).unwrap();
          if (response.success === true) {
            showToast('success', response.message);
            await dispatch(getUser()).unwrap();
          } else {
            showToast('error', response.message);
          }
        }
      }
      catch (error) {
        showToast('error', error.message);
        setBtnDisable(false);
      };
    }
  }

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileContent}>

        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>My Profile</h1>
          <p className={styles.pageSubtitle}>
            Manage your personal information and account security.
          </p>
        </div>

        {isUserDetailsLoading ? (
          <BeatLoader color="#1d0c50" />
        ) : (
          <>
            <div className={styles.profileCard}>
              <h1 className={`body-title`}>Hello {""}{details?.full_name}!</h1>
              <div className={styles.formContainer}>
                <form className={styles.paymentForm}>
                  <div className={styles.profileRow}>
                    <div className={styles.profilePicture}>
                      <img src={previewEditThumbnailImage ? previewEditThumbnailImage : details?.image_url ? details?.image_url : DefaultImage} className={styles.imageSec} alt='Profile' />
                      {imageUpdateStatus === 'loading' ?
                        <label className={styles.uploadIcon}>
                          <ClipLoader size={20} />
                        </label> :
                        <label className={styles.uploadIcon}>
                          <FontAwesomeIcon icon={faImage} color='gray' />
                          <input
                            type="file"
                            accept="image/*"
                            className={styles.fileInput}
                            onChange={handleFileChange}
                          />
                        </label>
                      }
                    </div>
                  </div>
                  <div className={styles.inputRow}>
                    <div className={styles.paymentInputFormGroup}>
                      <div className={`${styles.paymentInputGroup} input-group`}>
                        <div>
                          <label className="form-label">Full Name</label>
                        </div>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name || ''}
                          onChange={handleChange}
                          className={`${styles.profileInput} form-input-default`}
                        />
                      </div>
                      {profileErrors.full_name && (
                        <p className={`text-danger`}>{profileErrors.full_name}</p>
                      )}
                    </div>
                  </div>
                  <div className={styles.inputRow}>
                    <div className={styles.paymentInputFormGroup}>
                      <div className={`${styles.paymentInputGroup} input-group`}>
                        <div>
                          <label className="form-label">Email</label>
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleChange}
                          className={`${styles.profileInput} form-input-default`}
                        />
                      </div>
                      {profileErrors.email && (
                        <p className={`text-danger`}>{profileErrors.email}</p>
                      )}
                    </div>
                    <div className={styles.paymentInputFormGroup}>
                      <div className={`${styles.paymentInputGroup} input-group`}>
                        <div>
                          <label className="form-label">Phone Number</label>
                        </div>
                        <input
                          type="text"
                          name="phone_number"
                          value={formData.phone_number || ''}
                          onChange={handleChange}
                          className={`${styles.profileInput} form-input-default`}
                        />
                      </div>
                      {profileErrors.phone_number && (
                        <p className={`text-danger`}>{profileErrors.phone_number}</p>
                      )}
                    </div>
                  </div>
                  <button type="button" className={styles.paymentBtn} onClick={editProfile} disabled={disabledBtn}>
                    {isDetailsLoading ? <BeatLoader color='#ffffff' /> : `Save`}
                  </button>
                </form>
              </div>
            </div>
            <div className={styles.passwordCard}>

              <div className={styles.sectionHeader}>
                <h2 className={styles.passwordTitle}>
                  Change Password
                </h2>

                <p className={styles.sectionDescription}>
                  Choose a strong password to keep your account secure.
                </p>
              </div>
              <h1 className={styles.passwordTitle}>Create New Password</h1>
              <div className={styles.formContainer}>
                <form className={styles.paymentForm}>
                  <div className={styles.inputRow}>
                    <div className={styles.paymentInputFormGroup}>
                      <div className={`${styles.paymentInputGroup} input-group`}>
                        <div>
                          <label className={`form-label`}>Password</label>
                        </div>
                        <input
                          type={passwordVisible ? "text" : "password"}
                          placeholder="********************"
                          value={credentials.newPassword || ''}
                          onChange={(e) => {
                            setCredentials({ ...credentials, newPassword: e.target.value });
                            setErrors({ ...errors, newPassword: '' })
                          }}
                          className={`${styles.profileInput} form-input-default`}
                        />
                        {!passwordVisible ?
                          <FontAwesomeIcon icon={faEyeSlash} style={{ marginLeft: '-40px' }} onClick={() => setPasswordVisible(true)} /> :
                          <FontAwesomeIcon icon={faEye} style={{ marginLeft: '-40px' }} onClick={() => setPasswordVisible(false)} />}
                      </div>
                      {errors.newPassword && (
                        <p className={`text-danger`}>{errors.newPassword}</p>
                      )}
                    </div>
                    <div className={styles.paymentInputFormGroup}>
                      <div className={` ${styles.paymentInputGroup} input-group`}>
                        <div>
                          <label className={`form-label`}>Confirm Password</label>
                        </div>
                        <input
                          type={confirmPasswordVisible ? "text" : "password"}
                          placeholder="********************"
                          value={credentials.confirmPassword || ''}
                          onChange={(e) => {
                            setCredentials({ ...credentials, confirmPassword: e.target.value });
                            setErrors({ ...errors, confirmPassword: '' })
                          }}
                          className={`${styles.profileInput} form-input-default`}
                        />
                        {!confirmPasswordVisible ?
                          <FontAwesomeIcon icon={faEyeSlash} style={{ marginLeft: '-40px' }} onClick={() => setConfirmPasswordVisible(true)} /> :
                          <FontAwesomeIcon icon={faEye} style={{ marginLeft: '-40px' }} onClick={() => setConfirmPasswordVisible(false)} />}
                      </div>
                      {errors.confirmPassword && (
                        <p className={`text-danger`}>{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>
                  <button type="button" className={styles.paymentBtn} onClick={handlePasswordChange} disabled={disabledBtn}>
                    {isChangingPassword ? <BeatLoader color='#ffffff' /> : `Save Password`}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;